import React from 'react';
import { motion } from 'framer-motion';
import {
    DocumentTextIcon,
    ChatBubbleLeftRightIcon,
    UserGroupIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
    Squares2X2Icon
} from '@heroicons/react/24/outline';

const AdminDashboardStats = ({ stats }) => {
    // Calculate max value for chart scaling
    const maxActivity = stats.daily_activity ? Math.max(...stats.daily_activity.map(d => d.count)) : 0;

    const statCards = [
        {
            title: 'Traffic',
            subtitle: `Total Site Visits`,
            value: (stats.total_site_visits || 0).toLocaleString(),
            trend: stats.total_site_visits > 0 ? '+100%' : '0%', // Simplified trend for visits
            trendUp: true,
            trendText: `Across all pages`,
            icon: <Squares2X2Icon className="h-6 w-6" />,
            isDark: true
        },
        {
            title: 'Engagement',
            subtitle: `${stats.total_likes || 0} Likes & ${stats.total_comments || 0} Comments`,
            value: (stats.total_engagement || 0).toLocaleString(),
            trend: `${stats.comments_this_week > 0 ? '+' : ''}${stats.comments_this_week || 0}`,
            trendUp: true,
            trendText: `New comments this week`,
            icon: <ChatBubbleLeftRightIcon className="h-6 w-6" />,
            isDark: false
        },
        {
            title: 'Community',
            subtitle: `Registered Members`,
            value: (stats.total_users || 0).toLocaleString(),
            trend: `+${stats.new_users_this_week || 0}`,
            trendUp: true,
            trendText: `Joined this week`,
            icon: <UserGroupIcon className="h-6 w-6" />,
            isDark: false
        },
        {
            title: 'Insights',
            subtitle: `${stats.total_blogs || 0} Articles`,
            value: (stats.total_blogs || 0).toLocaleString(),
            trend: `${stats.total_categories || 0} Topics`,
            trendUp: true,
            trendText: 'Across all blogs',
            icon: <DocumentTextIcon className="h-6 w-6" />,
            isDark: false
        }
    ];

    return (
        <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {statCards.map((stat, index) => (
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={`p-6 rounded-[2.5rem] shadow-sm transition-all hover:shadow-xl hover:shadow-agro-green-dark/5 ${stat.isDark ? 'bg-agro-green-dark text-white' : 'bg-white text-agro-black'
                            } border border-agro-green-light/5`}
                    >
                        <div className="flex justify-between items-start mb-6">
                            <div className={`p-3 rounded-2xl ${stat.isDark ? 'bg-white/10' : 'bg-agro-green-light/10'
                                }`}>
                                <div className={stat.isDark ? 'text-agro-lime' : 'text-agro-green'}>
                                    {stat.icon}
                                </div>
                            </div>
                        </div>

                        <div className="space-y-1 mb-6">
                            <h3 className={`text-sm font-bold font-sans uppercase tracking-wider ${stat.isDark ? 'text-white' : 'text-agro-green/80'}`}>
                                {stat.title}
                            </h3>
                            <p className={`text-xs ${stat.isDark ? 'text-white/70' : 'text-agro-green/60'}`}>
                                {stat.subtitle}
                            </p>
                        </div>

                        <div className="flex items-end justify-between">
                            <h2 className="text-3xl font-serif">{stat.value}</h2>
                        </div>

                        <div className="mt-4 flex items-center space-x-2 text-[10px] uppercase tracking-widest font-bold">
                            <span className={`flex items-center ${stat.trendUp ? (stat.isDark ? 'text-agro-lime' : 'text-agro-green') : (stat.isDark ? 'text-white/80' : 'text-red-500')
                                }`}>
                                {stat.trendUp ? <ArrowTrendingUpIcon className="h-3 w-3 mr-1" /> : <ArrowTrendingDownIcon className="h-3 w-3 mr-1" />}
                                {stat.trend}
                            </span>
                            <span className={`${stat.isDark ? 'text-white/30' : 'text-agro-green/30'}`}>
                                {stat.trendText}
                            </span>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white p-8 rounded-[2.5rem] shadow-sm border border-agro-green-light/5">
                    <div className="flex justify-between items-center mb-10">
                        <div>
                            <h3 className="text-xl font-serif text-agro-black">Engagement Overview</h3>
                            <p className="text-xs text-agro-green/60 uppercase tracking-widest font-bold mt-1">Interactions over the last 12 days</p>
                        </div>
                        <div className="flex space-x-2">
                            <button className="px-4 py-2 text-[10px] uppercase tracking-widest font-bold border border-agro-green-light/10 rounded-xl hover:bg-agro-green-light/5 transition-colors">History</button>
                        </div>
                    </div>
                    <div className="overflow-x-auto custom-scrollbar pb-10 w-full">
                        <div className="h-64 flex items-end justify-between space-x-2 px-4 border-b border-agro-green-light/5 min-w-[600px] md:min-w-0">
                            {/* Activity Bars */}
                            {stats.daily_activity && stats.daily_activity.map((day, i) => {
                                const heightPercentage = maxActivity > 0 ? (day.count / maxActivity) * 80 + 10 : 10;
                                return (
                                    <div key={i} className="w-full bg-agro-green-light/5 rounded-t-2xl relative group h-full flex flex-col items-center justify-end">
                                        <motion.div
                                            initial={{ height: 0 }}
                                            animate={{ height: `${heightPercentage}%` }}
                                            transition={{ duration: 1, delay: i * 0.05, ease: [0.4, 0, 0.2, 1] }}
                                            className="w-full bg-agro-green/10 rounded-t-2xl transition-all duration-500 group-hover:bg-agro-green relative cursor-pointer"
                                        >
                                            <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all bg-agro-green-dark shadow-2xl text-white text-[10px] font-bold px-3 py-2 rounded-xl whitespace-nowrap z-10 translate-y-2 group-hover:translate-y-0">
                                                {day.count} interactions
                                            </div>
                                        </motion.div>
                                        <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 text-[9px] font-bold text-agro-green/30 uppercase tracking-tighter rotate-45 origin-left whitespace-nowrap">
                                            {day.date}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-agro-green-light/5">
                    <h3 className="text-xl font-serif text-agro-black mb-1">Impact Feed</h3>
                    <p className="text-xs text-agro-green/60 uppercase tracking-widest font-bold mb-10">Popular content distribution</p>

                    <div className="relative h-48 w-48 mx-auto mb-10">
                        <svg viewBox="0 0 36 36" className="w-full h-full transform -rotate-90">
                            <path className="text-agro-green-light/5" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="2.5" />
                            {stats.top_blogs && stats.top_blogs.length > 0 && (
                                <motion.path
                                    initial={{ strokeDasharray: "0, 100" }}
                                    animate={{ strokeDasharray: `${stats.top_blogs[0].percentage}, 100` }}
                                    transition={{ duration: 2, ease: "easeOut" }}
                                    className="text-agro-green"
                                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                />
                            )}
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center flex-col">
                            <span className="text-3xl font-serif text-agro-black">
                                {stats.total_engagement || 0}
                            </span>
                            <span className="text-[9px] text-agro-green/40 uppercase tracking-widest font-bold mt-1">Total Hits</span>
                        </div>
                    </div>

                    <div className="space-y-6">
                        {stats.top_blogs && stats.top_blogs.slice(0, 3).map((blog, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="space-y-3"
                            >
                                <div className="flex items-center justify-between text-[10px] items-center">
                                    <div className="flex items-center truncate max-w-[80%]">
                                        <span className={`w-1.5 h-1.5 ${index === 0 ? 'bg-agro-green' : index === 1 ? 'bg-agro-lime' : 'bg-agro-green-light'} rounded-full mr-2 flex-shrink-0`}></span>
                                        <span className="text-agro-green-dark truncate font-bold uppercase tracking-wider">{blog.name}</span>
                                    </div>
                                    <span className="font-bold text-agro-green">{blog.percentage}%</span>
                                </div>
                                <div className="w-full bg-agro-green-light/5 h-1 rounded-full overflow-hidden">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${blog.percentage}%` }}
                                        transition={{ duration: 1.5, delay: 0.5 + (index * 0.1), ease: "easeOut" }}
                                        className={`${index === 0 ? 'bg-agro-green' : index === 1 ? 'bg-agro-lime' : 'bg-agro-green-light'} h-full rounded-full`}
                                    />
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboardStats;
